if (!customElements.get('wls-product-form')) {
  class WLSProductForm extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        inputQuantity: 'input[name="quantity"]',
        inputVariantId: 'input[name="id"]',
        inputSellingPlan: 'input[name="selling_plan"]',
        inputPurchaseType: 'input[name="purchase-type"]',
        productFormFrequency: 'wls-product-form-frequency',
        totalPrice: 'js-total-price',
        totalCompareAtPrice: 'js-total-compare-at-price',
        frequencyPrice: 'js-frequency-price',
        frequencyCompareAtPrice: 'js-frequency-compare-at-price',
        loading: 'loading',
        submitButton: 'button[type="submit"]',
        errorMessage: 'js-error-message',
        wlsForm: 'js-wls-product-form',
      };

      this.purchaseType = {
        OTP: 'one-time',
        SUB: 'subscription',
      };

      this.boundHandleInputChange = this._handleInputChange.bind(this);
      this.boundHandleSubmit = this._handleSubmit.bind(this);
      this.boundHandleMutation = this._handleMutation.bind(this);
    }

    /**
     * Get elements for form elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        form: this.querySelector('form'),
        quantityInput: this.querySelector(this.classes.inputQuantity),
        variantIdInput: this.querySelector(this.classes.inputVariantId),
        sellingPlanInput: this.querySelector(this.classes.inputSellingPlan),
        purchaseTypeInputs: this.querySelectorAll(this.classes.inputPurchaseType),
        productFormFrequency: this.querySelector(this.classes.productFormFrequency),
        totalPrice: this.querySelector(`.${this.classes.totalPrice}`),
        totalCompareAtPrice: this.querySelector(`.${this.classes.totalCompareAtPrice}`),
        frequencyPrice: this.querySelector(`.${this.classes.frequencyPrice}`),
        frequencyCompareAtPrice: this.querySelector(`.${this.classes.frequencyCompareAtPrice}`),
        submitButtons: this.querySelectorAll(this.classes.submitButton),
        errorMessage: this.querySelector(`.${this.classes.errorMessage}`),
        wlsForm: this.querySelector(`${this.classes.wlsForm}`),
      };
    }

    /**
     * Get data attributes from the element
     * @returns {object} - Object containing data attributes
     */
    get dataAttributes() {
      return {
        productForm: this.getAttribute('data-product-form'),
        formDisabled: this.getAttribute('data-form-disabled'),
        initPurchaseType: this.elements.productFormFrequency?.getAttribute('data-init-purchase-type') || 'one-time',
        ctaDirectCheckout: this.getAttribute('data-wls-cta-direct-checkout'),
        preventOpenCartForMobile: this.hasAttribute('data-prevent-open-cart-for-mobile'),
      };
    }

    /**
     * Get inputs with their event listeners configuration
     * @returns {Array<object>} - Array of input elements with their events
     */
    get inputsWithEvents() {
      return [
        { element: this.elements.quantityInput, events: ['change', 'input'] },
        { element: this.elements.variantIdInput, events: ['change', 'input'] },
        { element: this.elements.sellingPlanInput, events: ['change', 'input'] },
        { element: this.elements.purchaseTypeInputs, events: ['change', 'input'] },
      ];
    }

    /**
     * Get the current product variant object from global window data
     * @returns {object|undefined} - The current variant object or undefined
     */
    get currentProductVariantObject() {
      return window.productVariants?.find((variant) => variant.id.toString() === this.currentFormState.variantId);
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      this._initializeFormState();
      this._bindEvents();
      this._setupMutationObserver();
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      this._unbindEvents();
      this._disconnectMutationObserver();
    }

    /**
     * Public method to reinitialize the form
     * Called when the form is moved in the DOM (e.g., modal move mode)
     * @returns {void}
     */
    reinitialize() {
      // Unbind first to prevent duplicate listeners
      this._unbindEvents();
      this._disconnectMutationObserver();

      // Reinitialize
      this._initializeFormState();
      this._bindEvents();
      this._setupMutationObserver();
    }

    /**
     * Initialize the form state from DOM elements
     * @returns {void}
     */
    _initializeFormState() {
      this.currentFormState = {
        quantity: this.elements.quantityInput?.value || 1,
        variantId: this.elements.variantIdInput?.value || '',
        sellingPlan: this.elements.sellingPlanInput?.value || '',
        purchaseType: this.dataAttributes.initPurchaseType || '',
      };
    }

    /**
     * Bind event listeners to form inputs
     * @returns {void}
     */
    _bindEvents() {
      this.inputsWithEvents.forEach(({ element, events }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element.length !== undefined ? Array.from(element) : [element];
          elements.forEach((el) => {
            if (el) {
              events.forEach((event) => {
                el.addEventListener(event, this.boundHandleInputChange);
              });
            }
          });
        }
      });

      if (this.elements.form) {
        this.elements.form.addEventListener('submit', this.boundHandleSubmit);
      }
    }

    /**
     * Unbind event listeners from form inputs
     * @returns {void}
     */
    _unbindEvents() {
      this.inputsWithEvents.forEach(({ element, events }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element.length !== undefined ? Array.from(element) : [element];
          elements.forEach((el) => {
            if (el) {
              events.forEach((event) => {
                el.removeEventListener(event, this.boundHandleInputChange);
              });
            }
          });
        }
      });

      if (this.elements.form) {
        this.elements.form.removeEventListener('submit', this.boundHandleSubmit);
      }
    }

    /**
     * Handle input change events
     * @param {Event} e - The input change event
     * @returns {void}
     */
    _handleInputChange(e) {
      const input = e.target;
      const inputName = input.name;
      const inputValue = input.value;

      // Update the currentFormState based on input name
      this._updateCurrentState(inputName, inputValue);
    }

    /**
     * Update the current state based on field name and value
     * @param {string} fieldName - The name of the field being updated
     * @param {string} value - The new value
     * @returns {void}
     */
    _updateCurrentState(fieldName, value) {
      const { frequencyPrice, frequencyCompareAtPrice, totalPrice, totalCompareAtPrice } = this.elements;
      switch (fieldName) {
        case 'quantity':
          this.currentFormState.quantity = parseInt(value) || 1;
          this._updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
        case 'id':
          this.currentFormState.variantId = value;
          this._updatePrices({
            priceElement: frequencyPrice,
            compareAtElement: frequencyCompareAtPrice,
          });
          this._updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          this.currentProductVariantObject;
          break;
        case 'selling_plan':
          this.currentFormState.sellingPlan = value;
          if (this.currentFormState.purchaseType === this.purchaseType.SUB) {
            this._updatePrices({
              priceElement: frequencyPrice,
              compareAtElement: frequencyCompareAtPrice,
            });
          }
          this._updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
        case 'purchase-type':
          this.currentFormState.purchaseType = value;
          this._updatePrices({
            priceElement: totalPrice,
            compareAtElement: totalCompareAtPrice,
            multiplier: this.currentFormState.quantity,
          });
          break;
      }
    }

    /**
     * Update price elements based on current state
     * @param {object} params - Price update parameters
     * @param {HTMLElement} params.priceElement - The price element to update
     * @param {HTMLElement} [params.compareAtElement] - The compare at price element to update
     * @param {number} [params.multiplier=1] - The multiplier for the price
     * @returns {void}
     */
    _updatePrices({ priceElement, compareAtElement, multiplier = 1 }) {
      const variant = this.currentProductVariantObject;
      const { purchaseType, sellingPlan } = this.currentFormState;

      if (!variant || !priceElement) return;

      const isSubscription = purchaseType === this.purchaseType.SUB;
      const allocation = isSubscription
        ? variant.selling_plan_allocations.find((a) => a.selling_plan_id.toString() === sellingPlan)
        : null;

      const toInt = (x) => (x == null ? null : Number(x));
      const price = toInt(allocation?.price ?? variant.price);
      const compareAt = toInt(allocation?.compare_at_price ?? variant.compare_at_price);

      priceElement.textContent = this._formatMoney(price * multiplier);

      if (compareAtElement) {
        if (compareAt && compareAt !== price) {
          compareAtElement.textContent = this._formatMoney(compareAt * multiplier);
        } else {
          compareAtElement.textContent = '';
        }
      }
    }

    /**
     * Handle form submit — mirrors product-form.js: FormData + fetchConfig + cart.renderContents
     * @param {Event} e - The submit event
     * @returns {Promise<void>}
     */
    async _handleSubmit(e) {
      e.preventDefault();
      if (this.dataAttributes.formDisabled) return;

      if (e.submitter?.getAttribute('aria-disabled') === 'true') return;

      if (this.elements.wlsForm && typeof this.elements.wlsForm.validate === 'function') {
        const isValid = this.elements.wlsForm.validate();
        if (!isValid) return;
      }

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.elements.form);
      if (cart) {
        formData.append(
          'sections',
          cart.getSectionsToRender().map((section) => section?.section || section.id)
        );
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement?.(document.activeElement);
      }
      config.body = formData;

      try {
        this._addFormLoadingState(e.submitter);

        const response = await fetch(routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          const errorMessage = data.description || data.message || 'Unable to add this item to cart.';
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: data.errors || data.description,
            message: data.message,
          });
          this._renderErrorMessage(errorMessage);
          this.dispatchEvent(new CustomEvent('wls:add-to-cart-error', {
            bubbles: true,
            detail: { message: errorMessage },
          }));
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        const suppressDrawerMobile = this.dataAttributes.preventOpenCartForMobile && window.innerWidth < 1024;
        const suppressDrawer = suppressDrawerMobile;

        if (suppressDrawer) {
          // Update cart sections without opening the drawer
          const originalOpenMinicart = cart.openMinicart.bind(cart);
          cart.openMinicart = () => {};
          cart.renderContents(data);
          cart.openMinicart = originalOpenMinicart;
        } else {
          cart.renderContents(data);
        }

        // Always dispatch so WlsMobileQuickAdd can clean up (close dialog / show success screen)
        this.dispatchEvent(new CustomEvent('wls:add-to-cart-success', {
          bubbles: true,
          detail: { data, showSuccessScreen: suppressDrawer },
        }));

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: data,
        });
      } catch (error) {
        console.error('[WLSProductForm] Add to cart error:', error);
        this.dispatchEvent(new CustomEvent('wls:add-to-cart-error', {
          bubbles: true,
          detail: { message: 'Unable to add this item to cart. Please try again.' },
        }));
      } finally {
        this._removeFormLoadingState();
        if (this.elements.wlsForm && typeof this.elements.wlsForm.handleSuccess === 'function') {
          this.elements.wlsForm.handleSuccess();
        }
      }
    }

    /**
     * Setup mutation observer to watch for attribute changes
     * @returns {void}
     */
    _setupMutationObserver() {
      // Create observer to watch for attribute changes on inputs
      this.mutationObserver = new MutationObserver(this.boundHandleMutation);

      // Start observing all input elements for attribute changes
      const inputs = this.inputsWithEvents;
      inputs.forEach(({ element }) => {
        if (element) {
          // Check if element is a NodeList (array-like) or single element
          const elements = element instanceof NodeList ? [...element] : [element];
          elements.forEach((el) => {
            if (el) {
              this.mutationObserver.observe(el, {
                attributes: true,
                attributeFilter: ['value'],
                subtree: false,
              });
            }
          });
        }
      });
    }

    /**
     * Render error message
     * @param {string|string[]} message - The error message to display (string or array from API)
     * @returns {void}
     */
    _renderErrorMessage(message) {
      const el = this.elements.errorMessage;
      if (!el) return;

      const text = Array.isArray(message) ? message.join(' ') : (message || '');
      el.textContent = text;

      setTimeout(() => {
        el.textContent = '';
      }, 5000);
    }

    /**
     * Add loading state to form — blocks double-submit via aria-disabled
     * @returns {void}
     */
    _addFormLoadingState(submitter) {
      const { submitButtons, form } = this.elements;
      form.classList.add(this.classes.loading);
      if (submitter?.closest('.js-wls-variant-drawer, .js-quick-add-dialog')) {
        form.classList.add('loading--overlay');
      }
      submitButtons.forEach((button) => {
        button.classList.add(this.classes.loading);
        button.setAttribute('aria-disabled', 'true');
      });
    }

    /**
     * Remove loading state from form and notify the card so it can clear
     * loading from any quick-add buttons (variant btns, no-variant quick-add).
     * @returns {void}
     */
    _removeFormLoadingState() {
      const { submitButtons, form } = this.elements;
      form.classList.remove(this.classes.loading, 'loading--overlay');
      submitButtons.forEach((button) => {
        button.classList.remove(this.classes.loading);
        button.removeAttribute('aria-disabled');
      });
    }

    /**
     * Handle mutation events
     * @param {Array<MutationRecord>} mutations - The mutation records
     * @returns {void}
     */
    _handleMutation(mutations) {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const input = mutation.target;
          const inputName = input.name;
          const inputValue = input.value;

          // Update currentFormState when value attribute changes
          this._updateCurrentState(inputName, inputValue);
        }
      });
    }

    /**
     * Format money amount using theme format
     * @param {number} cents - The amount in ns {string} - Formatted money string
     */
    _formatMoney(cents) {
      const moneyFormat = window.theme?.moneyFormat || '${{amount}}';
      let formatted =
        window.WLSNHelpers.formatMoney(cents, moneyFormat) || `$${(cents / 100).toFixed(2)}`;
      // shop.money_format often wraps the amount in HTML; textContent must stay plain text
      if (formatted.includes('<')) {
        formatted = formatted.replace(/<[^>]*>/g, '').trim();
      }
      return formatted;
    }

    /**
     * Disconnect mutation observer
     * @returns {void}
     */
    _disconnectMutationObserver() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
    }
  }

  customElements.define('wls-product-form', WLSProductForm);
}
